import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Guards.css";

const Guards = () => {
  const [guards, setGuards] = useState([]);
  const [filteredGuards, setFilteredGuards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingGuard, setEditingGuard] = useState(null);
  const [editData, setEditData] = useState({ fullName: "", phoneNumber: "" });

  useEffect(() => {
    fetchGuards();
  }, []);

  const fetchGuards = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get("/api/admin/users/role/ROLE_GUARD", {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      const data = Array.isArray(res.data) ? res.data : [res.data];
      setGuards(data);
      setFilteredGuards(data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch guard data.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    if (!window.confirm("Are you sure you want to change guard status?")) return;

    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `/api/admin/users/${id}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchGuards();
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this guard?")) return;

    try {
      const token = localStorage.getItem("token");

      await axios.delete(`/api/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchGuards();
    } catch (err) {
      console.error(err);
      alert("Failed to delete guard");
    }
  };

  const handleEdit = (guard) => {
    setEditingGuard(guard);
    setEditData({
      fullName: guard.fullName,
      phoneNumber: guard.phoneNumber,
    });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `/api/admin/users/${editingGuard.id}`,
        editData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setEditingGuard(null);
      fetchGuards();
    } catch (err) {
      console.error(err);
      alert("Failed to update guard info");
    }
  };

  const handleCancelEdit = () => {
    setEditingGuard(null);
    setEditData({ fullName: "", phoneNumber: "" });
  };

  const handleResetPassword = async (guardId) => {
    const newPassword = prompt("Enter new password for this guard:");

    if (!newPassword) {
      alert("Password cannot be empty ?");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const res = await axios.put(
        `/api/admin/reset-password`,
        {},
        {
          params: { userId: guardId, newPassword },
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }
      );

      alert(res.data || "Password updated successfully ?");
    } catch (err) {
      console.error(err);
      alert("Failed to update password ?");
    }
  };

  useEffect(() => {
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = guards.filter(
      (g) =>
        g.fullName?.toLowerCase().includes(lowerQuery) ||
        g.phoneNumber?.toLowerCase().includes(lowerQuery) ||
        g.societyName?.toLowerCase().includes(lowerQuery)
    );
    setFilteredGuards(filtered);
  }, [searchQuery, guards]);

  return (
    <div className="guards-page">
      <h2>??? Guard Management</h2>
      <p>Assign, track, and manage all guards efficiently below.</p>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, phone, or society..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="loading">Loading guard data...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <div className="table-container">
          <table className="guards-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Full Name</th>
                <th>Phone</th>
                <th>Society</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredGuards.length > 0 ? (
                filteredGuards.map((g) => (
                  <tr key={g.id}>
                    <td>{g.id}</td>
                    <td>{g.fullName}</td>
                    <td>{g.phoneNumber}</td>
                    <td>{g.societyName}</td>
                    <td className={g.enabled ? "status-active" : "status-disabled"}>
                      {g.enabled ? "Active" : "Disabled"}
                    </td>
                    <td>
                      <button
                        className={`status-btn ${g.enabled ? "deactivate" : "activate"}`}
                        onClick={() => handleToggle(g.id)}
                      >
                        {g.enabled ? "Deactivate" : "Activate"}
                      </button>

                      <button className="edit-btn" onClick={() => handleEdit(g)}>
                        Edit
                      </button>

                      <button className="delete-btn" onClick={() => handleDelete(g.id)}>
                        Delete
                      </button>

                      <button
                        className="reset-btn"
                        onClick={() => handleResetPassword(g.id)}
                      >
                        Password
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", color: "#bbb" }}>
                    No guards found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editingGuard && (
        <div className="edit-modal">
          <div className="modal-content">
            <h3>Edit Guard Info</h3>
            <input
              type="text"
              placeholder="Full Name"
              value={editData.fullName}
              onChange={(e) => setEditData({ ...editData, fullName: e.target.value })}
            />
            <input
              type="text"
              placeholder="Phone Number"
              value={editData.phoneNumber}
              onChange={(e) => setEditData({ ...editData, phoneNumber: e.target.value })}
            />

            <div className="modal-actions">
              <button onClick={handleSaveEdit}>Save</button>
              <button onClick={handleCancelEdit}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Guards;
